//--------------------------------------------------------------------//
//******************** agentVoiceTab ***********************************//
//--------------------------------------------------------------------//
// Configures an agent's SIP voice-calling settings (agent.voice — see
// gudhub-agents' agents.database.js/agent-manager.js). Per-field inline
// edit/save/cancel, same shape as agentInfoTab — each of the 9 voice fields
// is independently editable/saveable.
import agentVoiceTemplateUrl from './agent-voice.template.html';
import { GUDHUB_SETTINGS } from '../../../config.js';

// OpenAI names realtime models with "realtime" in the id (e.g.
// gpt-realtime-2.1); xAI names its speech-to-speech models with "voice"
// instead (e.g. grok-voice-latest, grok-voice-think-fast-1.0) — used to
// filter the live GET /providers/:id/models response down to just the
// realtime-capable ones. Whether either provider's models-list endpoint
// actually includes these ids at all is unconfirmed — fetchRealtimeModels()
// surfaces an explicit "none found" state rather than silently failing.
const REALTIME_NAME_HEURISTIC = { openai: 'realtime', grok: 'voice' };

// Hardcoded per public docs (neither provider exposes a fully reliable live
// "list voices" endpoint for the realtime/SIP `voice` param — xAI's
// GET /v1/tts/voices is TTS-catalog-shaped and OpenAI has no such endpoint at
// all — so this is a static list, not fetched). openai: the 10 voices
// documented as valid for Realtime sessions specifically (fable/nova/onyx
// are TTS-only and get rejected by the Realtime accept API — confirmed live
// 2026-07-30, see gudhub-agents' realtime-sip.js). marin/cedar are OpenAI's
// recommended realtime picks. grok: xAI's 5 core built-in voices (ara is the
// default).
const VOICE_NAMES = {
    openai: ['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'marin', 'cedar'],
    grok: ['ara', 'eve', 'leo', 'rex', 'sal']
};

angular.module('agentsTableModule')

.directive('agentVoiceTab', ['AgentVoiceService', 'GhDialog', function (AgentVoiceService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            agent: '='
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            // Agents created before voice calling existed have no agent.voice
            // at all — seed it so every field below has something to bind to.
            if (!$scope.agent.voice) {
                $scope.agent.voice = {
                    enabled: false,
                    ai_provider_id: null,
                    api_key: null,
                    model: null,
                    voice_name: null,
                    instructions: null,
                    provider_call_address: null,
                    webhook_secret: null,
                    expressive: false
                };
            }

            $scope.editMode = {
                ai_provider_id: false,
                api_key: false,
                model: false,
                voice_name: false,
                instructions: false,
                provider_call_address: false,
                webhook_secret: false
            };

            $scope.originalValues = angular.copy($scope.agent.voice);

            $scope.showVoiceApiKey = false;
            $scope.showVoiceWebhookSecret = false;
            $scope.validationError = '';

            $scope.callingProviders = [];
            $scope.realtimeModels = [];
            $scope.loadingRealtimeModels = false;
            $scope.realtimeModelsError = '';

            const safeApply = function () {
                $scope.$applyAsync();
            };

            // The webhook a provider (OpenAI/xAI) posts realtime.call.incoming
            // to (see call-routes.js's registerCallWebhookRoutes) — deterministic
            // once a provider is picked, so no backend lookup is needed, just
            // the same server address this client already talks to for
            // everything else.
            $scope.webhookUrl = function () {
                if (!$scope.agent.voice.ai_provider_id) return '';
                return GUDHUB_SETTINGS.agents_server_url + '/webhooks/calls/' + $scope.agent.voice.ai_provider_id;
            };

            $scope.copiedWebhookUrl = false;

            // Mirrors sip_trunks' trunk-info.tab.js's copySipServerUri.
            $scope.copyWebhookUrl = async function (event) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }

                const url = $scope.webhookUrl();
                if (!url) return;

                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(url);
                    } else {
                        const textarea = document.createElement('textarea');

                        textarea.value = url;
                        textarea.setAttribute('readonly', '');
                        textarea.style.position = 'absolute';
                        textarea.style.left = '-9999px';

                        document.body.appendChild(textarea);
                        textarea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textarea);
                    }

                    $scope.copiedWebhookUrl = true;
                    safeApply();

                    setTimeout(function () {
                        $scope.copiedWebhookUrl = false;
                        safeApply();
                    }, 1500);
                } catch (error) {
                    console.log('Webhook URL copy error:', error);
                }
            };

            const showError = function (error) {
                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="delete_agent-popup">
                                <div class="dialog-share-group">
                                    <p class="dialog-share-group__title">Error</p>
                                </div>
                                <div class="dialog-share-group__content">
                                    <p class="info">{{errorMessage}}</p>
                                    <div class="dialog-share-group__content__buttons">
                                        <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                    </div>
                                </div>
                            </div>
                        `
                    },
                    locals: { errorMessage: error.message },
                    controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                        $scope.errorMessage = errorMessage;
                    }]
                });
            };

            // Validates the same conditions agent-manager.js's POST /update
            // enforces server-side before allowing voice.enabled to be true —
            // used by toggleEnabled below.
            function validateEnabled() {
                const providerIsCallingCapable = $scope.callingProviders.some(function (p) {
                    return p.id === $scope.agent.voice.ai_provider_id;
                });

                if (!providerIsCallingCapable) {
                    return $scope.translate('A calling-capable provider is required when Voice Calling is enabled.');
                }
                if (!$scope.agent.voice.api_key) {
                    return $scope.translate('API Key is required when Voice Calling is enabled.');
                }
                if (!$scope.agent.voice.webhook_secret) {
                    return $scope.translate('Webhook Secret is required when Voice Calling is enabled.');
                }
                if (!$scope.agent.voice.model) {
                    return $scope.translate('Model is required when Voice Calling is enabled.');
                }
                if (!$scope.agent.voice.provider_call_address) {
                    const label = $scope.providerCallAddressLabel().replace(/:$/, '');
                    return label + ' ' + $scope.translate('is required when Voice Calling is enabled.');
                }
                return '';
            }

            // gh-boolean's internal md-switch already flips agent.voice.enabled
            // via ng-model before this bubbled click handler runs (same pattern
            // as agent-scheduling.tab.js's toggleTask) — an always-live toggle,
            // no edit/save/cancel step, matching how agent-scheduling/
            // agent-subscriptions handle their own boolean switches.
            $scope.toggleEnabled = function (e) {
                e.stopPropagation();
                $scope.validationError = '';

                if ($scope.agent.voice.enabled) {
                    const error = validateEnabled();
                    if (error) {
                        $scope.agent.voice.enabled = false;
                        $scope.validationError = error;
                        safeApply();
                        return;
                    }
                }

                AgentVoiceService.update({
                    agent_id: $scope.agent._id,
                    voice: { enabled: $scope.agent.voice.enabled }
                }).then(function (updatedAgent) {
                    Object.assign($scope.agent, updatedAgent);
                    safeApply();
                }).catch(function (error) {
                    $scope.agent.voice.enabled = !$scope.agent.voice.enabled;
                    $scope.validationError = error.message;
                    safeApply();
                });
            };

            // Same always-live toggle pattern as toggleEnabled above, minus
            // the validation step — expressive has no cross-field requirement,
            // it's just a delivery-style instruction gudhub-agents' call-listener.js
            // appends when the agent is on a call.
            $scope.toggleExpressive = function (e) {
                e.stopPropagation();
                $scope.validationError = '';

                AgentVoiceService.update({
                    agent_id: $scope.agent._id,
                    voice: { expressive: $scope.agent.voice.expressive }
                }).then(function (updatedAgent) {
                    Object.assign($scope.agent, updatedAgent);
                    safeApply();
                }).catch(function (error) {
                    $scope.agent.voice.expressive = !$scope.agent.voice.expressive;
                    $scope.validationError = error.message;
                    safeApply();
                });
            };

            // Fetched proactively on load so the Provider dropdown is ready
            // the moment the user clicks its pencil.
            AgentVoiceService.getCallingProviders().then(function (providers) {
                $scope.callingProviders = providers || [];
                safeApply();
            }).catch(function (error) {
                console.error('Failed to load calling-capable providers:', error);
            });

            $scope.providerName = function (providerId) {
                const match = $scope.callingProviders.find(function (p) {
                    return p.id === providerId;
                });
                return match ? match.name : '';
            };

            $scope.voiceNameOptions = function () {
                return VOICE_NAMES[$scope.agent.voice.ai_provider_id] || [];
            };

            $scope.providerCallAddressLabel = function () {
                if ($scope.agent.voice.ai_provider_id === 'openai') return $scope.translate('OpenAI Project ID:');
                if ($scope.agent.voice.ai_provider_id === 'grok') return $scope.translate('xAI Phone Number:');
                return $scope.translate('Provider Call Address:');
            };

            // Fetches the live model list for the agent's currently-saved
            // voice provider/api_key (same GET /providers/:id/models call
            // agent-info.tab.js's fetchModels() makes for chat models),
            // filtered to realtime-looking ids. Depends on both being
            // already saved — unlike chat's model list, this can't run
            // proactively on load since voice.api_key may not exist yet the
            // first time this tab is opened.
            async function fetchRealtimeModels() {
                const providerId = $scope.agent.voice.ai_provider_id;
                const apiKey = $scope.agent.voice.api_key || '';

                if (!providerId) {
                    $scope.realtimeModels = [];
                    $scope.realtimeModelsError = $scope.translate('Select a Provider before selecting a model.');
                    return;
                }
                if (!apiKey) {
                    $scope.realtimeModels = [];
                    $scope.realtimeModelsError = $scope.translate('Set an API Key before selecting a model.');
                    return;
                }

                $scope.loadingRealtimeModels = true;
                $scope.realtimeModelsError = '';
                $scope.realtimeModels = [];

                try {
                    const token = await gudhub.getToken();
                    const url = GUDHUB_SETTINGS.agents_server_url + '/providers/' + encodeURIComponent(providerId) +
                        '/models?api_key=' + encodeURIComponent(apiKey) + '&access_token=' + encodeURIComponent(token);

                    const response = await fetch(url);

                    if (!response.ok) {
                        const errBody = await response.json().catch(() => ({}));
                        throw new Error(errBody.message || errBody.error || 'Failed to load models.');
                    }

                    const rawModels = await response.json();
                    const needle = REALTIME_NAME_HEURISTIC[providerId] || 'realtime';
                    $scope.realtimeModels = rawModels
                        .filter(function (m) {
                            return (m.id || '').toLowerCase().includes(needle);
                        })
                        .map(function (m) {
                            return { name: m.name, value: m.id };
                        });

                    if (!$scope.realtimeModels.length) {
                        $scope.realtimeModelsError = $scope.translate('No realtime models found for this provider/key.');
                    }
                } catch (error) {
                    $scope.realtimeModelsError = error.message || $scope.translate('Failed to load models.');
                } finally {
                    $scope.loadingRealtimeModels = false;
                    safeApply();
                }
            }

            // Enters edit mode for a single field — stores its original value
            // so Cancel can revert without a server round-trip.
            $scope.enableEdit = function (field) {
                $scope.originalValues[field] = $scope.agent.voice[field];
                $scope.editMode[field] = true;
                $scope.validationError = '';

                if (field === 'model' && !$scope.realtimeModels.length && !$scope.loadingRealtimeModels) {
                    fetchRealtimeModels();
                }

                safeApply();
            };

            // Exits edit mode and restores the field to its original value.
            $scope.cancelEdit = function (field) {
                $scope.agent.voice[field] = $scope.originalValues[field];
                $scope.editMode[field] = false;
                $scope.validationError = '';
                safeApply();
            };

            // Saves a single field to the server. Every field here is always
            // independently valid to save — `enabled` is handled separately
            // by toggleEnabled above, since it's the one field whose validity
            // depends on the others already being saved.
            $scope.saveField = async function (field) {
                $scope.validationError = '';

                try {
                    const updatedAgent = await AgentVoiceService.update({
                        agent_id: $scope.agent._id,
                        voice: { [field]: $scope.agent.voice[field] }
                    });

                    Object.assign($scope.agent, updatedAgent);
                    $scope.originalValues[field] = $scope.agent.voice[field];
                    $scope.editMode[field] = false;

                    // Changing either invalidates whatever model list was
                    // loaded for the previous provider/key.
                    if (field === 'ai_provider_id' || field === 'api_key') {
                        fetchRealtimeModels();
                    }

                    safeApply();
                } catch (error) {
                    showError(error);
                }
            };
        }],
        templateUrl: agentVoiceTemplateUrl
    };
}]);
